import Errors from './Errors'
import axios from 'axios'
import store from '../store'
import { Cookies, Dialog } from 'quasar'
import { version } from '../../package.json'
import { i18nInstance } from '../boot/i18n'

class Form {
  static get config () {
    return {
      headers: {
        Authorization: 'Bearer ' + store().getters.access_token,
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Client-Version': version,
        'X-Language': store().getters.lang,
        withCredentials: true
      }
    }
  }

  /**
   * Create a new Form instance.
   *
   * @param {object} data
   * @param config
   * @param responseType
   */
  constructor (data = {}, config = null, responseType = 'json') {
    this.originalData = data
    for (const field in data) {
      this[field] = data[field]
    }
    this.config = config || Form.config
    this.responseType = responseType
    this.errors = new Errors()
    this.base_url = process.env.API
  }

  /**
   * Fetch all relevant data for the form.
   */
  data () {
    const data = {}
    for (const property in this.originalData) {
      data[property] = this[property]
    }
    return data
  }

  /**
   * Fetch all relevant data as a formData object.
   */
  formData (fileFields = []) {
    const fd = new FormData()
    for (const field in this.data()) {
      if (fileFields.includes(field)) {
        for (const file in this.data()[field]) {
          fd.append(file, this.data()[field][file])
        }
      } else {
        fd.append(field, this.data()[field])
      }
    }

    return fd
  }

  /**
   * Reset the form fields.
   */
  reset () {
    for (const field in this.originalData) {
      this[field] = ''
    }

    this.errors.clear()
  }

  /**
   * Send a GET request to the given URL.
   * .
   * @param {string} url
   */

  get (url) {
    return this.submit('get', url)
  }

  /**
   * Send a POST request to the given URL.
   * .
   * @param {string} url
   * @param data
   */
  post (url, data) {
    return this.submit('post', url, data)
  }

  /**
   * Send a PUT request to the given URL.
   * .
   * @param {string} url
   */
  put (url) {
    return this.submit('put', url)
  }

  /**
   * Send a PATCH request to the given URL.
   * .
   * @param {string} url
   */
  patch (url) {
    return this.submit('patch', url)
  }

  /**
   * Send a DELETE request to the given URL.
   * .
   * @param {string} url
   */
  delete (url) {
    return this.submit('delete', url)
  }

  /**
   * Submit the form.
   *
   * @param {string} requestType
   * @param {string} url
   * @param {object} data
   * @param {object} config
   * @param {string} responseType
   */
  submit (requestType, url, data = this.data(), config = this.config, responseType = this.responseType) {
    return new Promise((resolve, reject) => {
      const options = {
        url: url,
        method: requestType,
        data: data,
        baseURL: this.base_url,
        responseType: responseType
      }
      Object.assign(options, config)

      axios(options).then(response => {
        this.forceLogOutBlockedUsers(response)
        // this.checkVersion(url, response)
        this.onSuccess(response.data)
        resolve(response.data)
      }).catch(error => {
        switch (error.response.status) {
          case 401:
            this.forceLogOut()
            break
          case 422:
            this.onFail(error.response.data.errors)
            break
          case 426:
            Dialog.create({
              title: i18nInstance.t('update_required_title'),
              message: i18nInstance.t('update_required_message'),
              persistent: true,
              ok: {
                label: i18nInstance.t('logout'),
                color: 'negative'
              }
            }).onOk(() => {
              this.forceLogOut()
            })

            break
          case 500:
            reject({ data: { error: 'Server Error' } })
            break
        }
        reject(error.response)
      })
    })
  }

  /**
   * Handle a successful form submission.
   *
   * @param {object} data
   */
  onSuccess (data) {
    this.reset()
  }

  /**
   * Handle a failed form submission.
   *
   * @param {object} errors
   */
  onFail (errors) {
    this.errors.record(errors)
  }

  fill (data) {
    this.originalData = data
    for (const field in this.originalData) {
      this[field] = data[field]
    }
  }

  merge (data) {
    for (const field in data) {
      this.originalData[field] = data[field]
      this[field] = data[field]
    }
  }

  forceLogOut () {
    Cookies.remove('system_token')
    window.location.href = '/auth/login'
  }

  checkVersion (url, response) {
    if (Cookies.get('version') !== response.headers['x-app-version'] && url === 'login') {
      Cookies.set('version', response.headers['x-app-version'])
      window.location.reload()
    }
  }

  forceLogOutBlockedUsers (response) {
    if (response.headers['x-user-blocked'] === '1') {
      this.forceLogOut()
    }
  }

  forceRedirect (path) {
    window.$router.push(path)
  }
}

export default Form
